import { useEffect, useRef, useState } from "react";
import { Award, BookOpenCheck, Check, GraduationCap, ListChecks, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fileUrl } from "@/lib/api";
import { badge, btnPrimary, inputCls, Skeleton } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const TYPES = [["video", "Video YouTube"], ["text", "Teks"], ["pdf", "PDF"], ["audio", "Audio"], ["link", "Link"], ["quiz", "Kuis"]];
const emptyCourse = { title: "", description: "", category: "", instructor: "", cover_url: "", visibility: "all", grow_class_id: "", prereq_course_id: "", order: 0, passing_grade: 70, published: false, lessons: [] };
const newQuestion = () => ({ q: "", options: ["", "", "", ""], answer: 0 });

function QuizEditor({ quiz, onChange, ns = "q" }) {
  const qz = quiz || { passing_grade: 70, questions: [] };
  const set = (patch) => onChange({ ...qz, ...patch });
  const setQ = (i, patch) => set({ questions: qz.questions.map((q, k) => (k === i ? { ...q, ...patch } : q)) });
  return (
    <div className="space-y-3 rounded-2xl bg-slate-50 p-3" data-testid="quiz-editor">
      <label className="flex items-center gap-2 text-xs font-bold text-slate-500">Nilai kelulusan
        <input type="number" min={0} max={100} className={`${inputCls} !w-20 !p-2`} value={qz.passing_grade} onChange={(e) => set({ passing_grade: Number(e.target.value) })} data-testid="quiz-passing-input" />
      </label>
      {qz.questions.map((q, i) => (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`quiz-question-${i}`}>
          <div className="flex gap-2">
            <input className={inputCls} placeholder={`Pertanyaan ${i + 1}`} value={q.q} onChange={(e) => setQ(i, { q: e.target.value })} data-testid={`quiz-q-text-${i}`} />
            <button type="button" onClick={() => set({ questions: qz.questions.filter((_, k) => k !== i) })} className="text-red-400 hover:text-red-600" data-testid={`quiz-q-del-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          {q.options.map((opt, j) => (
            <div key={j} className="flex items-center gap-2">
              <input type="radio" name={`ans-${ns}-${i}`} checked={q.answer === j} onChange={() => setQ(i, { answer: j })} data-testid={`quiz-q-answer-${i}-${j}`} title="Jawaban benar" />
              <input className={`${inputCls} !py-2 text-sm ${q.answer === j ? "!border-emerald-500 !bg-emerald-50 font-bold" : ""}`} placeholder={`Opsi ${j + 1}`} value={opt} onChange={(e) => setQ(i, { options: q.options.map((o, k) => (k === j ? e.target.value : o)) })} data-testid={`quiz-q-opt-${i}-${j}`} />
              {q.answer === j && <span className="shrink-0 text-[10px] font-black uppercase tracking-wider text-emerald-600 inline-flex items-center gap-1" data-testid={`quiz-q-answer-label-${i}`}><Check className="w-3 h-3" />Benar</span>}
            </div>
          ))}
          <p className="text-[10px] text-slate-400">Klik radio di samping opsi untuk menandai jawaban benar (ditandai hijau).</p>
        </div>
      ))}
      <button type="button" onClick={() => set({ questions: [...qz.questions, newQuestion()] })} className="text-xs font-bold text-blue-600" data-testid="quiz-add-question">+ Tambah Pertanyaan</button>
    </div>
  );
}

function CourseEditor({ initial, categories, growClasses, allCourses, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setLesson = (i, patch) => setForm({ ...form, lessons: form.lessons.map((l, k) => (k === i ? { ...l, ...patch } : l)) });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, passing_grade: Number(form.passing_grade), order: Number(form.order) || 0,
        grow_class_id: form.grow_class_id || null, prereq_course_id: form.prereq_course_id || null, category: form.category || null };
      if (form.id) await api.put(`/admin/lms/courses/${form.id}`, payload);
      else await api.post("/admin/lms/courses", payload);
      toast.success("Course tersimpan");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-sm p-4" data-testid="course-editor">
      <form onSubmit={save} className="max-w-2xl mx-auto my-8 bg-white rounded-3xl p-6 md:p-8 shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-900">{form.id ? "Edit Course" : "Course Baru"}</h2>
          <button type="button" onClick={onClose} data-testid="course-editor-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <input className={inputCls} placeholder="Judul course" value={form.title} onChange={set("title")} required data-testid="course-title-input" />
        <textarea className={`${inputCls} min-h-20`} placeholder="Deskripsi" value={form.description || ""} onChange={set("description")} data-testid="course-desc-input" />
        <div className="grid md:grid-cols-2 gap-3">
          <select className={inputCls} value={form.category || ""} onChange={set("category")} data-testid="course-category-select">
            <option value="">Kategori…</option>{categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <input className={inputCls} placeholder="Instruktur (mis. Ps. Hendra)" value={form.instructor || ""} onChange={set("instructor")} data-testid="course-instructor-input" />
          <select className={inputCls} value={form.visibility} onChange={set("visibility")} data-testid="course-visibility-select">
            <option value="all">Terbuka (semua member E-Learning)</option><option value="enrolled">Khusus peserta ditugaskan</option>
          </select>
          <select className={inputCls} value={form.grow_class_id || ""} onChange={set("grow_class_id")} data-testid="course-grow-select">
            <option value="">Tanpa tautan Grow</option>{growClasses.map((g) => <option key={g.id} value={g.id}>{`Grow ${g.code} · ${g.title}`}</option>)}
          </select>
          <input className={inputCls} placeholder="URL cover (opsional)" value={form.cover_url || ""} onChange={set("cover_url")} data-testid="course-cover-input" />
          <select className={inputCls} value={form.prereq_course_id || ""} onChange={set("prereq_course_id")} data-testid="course-prereq-select">
            <option value="">Tanpa prasyarat course</option>
            {(allCourses || []).filter((c) => c.id !== form.id).map((c) => <option key={c.id} value={c.id}>{`Wajib selesai dulu: ${c.title}`}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-500">Urutan tampil
            <input type="number" min={0} className={`${inputCls} !w-20 !p-2`} value={form.order ?? 0} onChange={set("order")} data-testid="course-order-input" />
          </label>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-500">Nilai kelulusan default
            <input type="number" min={0} max={100} className={`${inputCls} !w-20 !p-2`} value={form.passing_grade} onChange={set("passing_grade")} data-testid="course-passing-input" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold text-slate-700"><input type="checkbox" checked={form.published} onChange={set("published")} data-testid="course-published-check" /> Terbitkan (tampil di portal)</label>

        <div className="pt-2">
          <p className="text-sm font-extrabold text-slate-900 border-b border-slate-100 pb-2">Materi / Lessons ({form.lessons.length})</p>
          <div className="mt-3 space-y-3">
            {form.lessons.map((l, i) => (
              <div key={l.id || i} className="rounded-2xl border border-slate-200 p-3 space-y-2" data-testid={`lesson-editor-${i}`}>
                <div className="flex gap-2">
                  <select className={`${inputCls} !w-36`} value={l.type} onChange={(e) => setLesson(i, { type: e.target.value })} data-testid={`lesson-type-${i}`}>
                    {TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                  <input className={inputCls} placeholder="Judul materi" value={l.title} onChange={(e) => setLesson(i, { title: e.target.value })} required data-testid={`lesson-title-${i}`} />
                  <button type="button" onClick={() => setForm({ ...form, lessons: form.lessons.filter((_, k) => k !== i) })} className="text-red-400 hover:text-red-600" data-testid={`lesson-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                {["video", "pdf", "audio", "link"].includes(l.type) && <input className={inputCls} placeholder={l.type === "video" ? "URL YouTube" : "URL file/link"} value={l.url || ""} onChange={(e) => setLesson(i, { url: e.target.value })} data-testid={`lesson-url-${i}`} />}
                {l.type === "text" && <textarea className={`${inputCls} min-h-20`} placeholder="Isi materi teks" value={l.content || ""} onChange={(e) => setLesson(i, { content: e.target.value })} data-testid={`lesson-content-${i}`} />}
                {l.type !== "quiz" && <textarea className={`${inputCls} min-h-16`} placeholder="Tugas yang harus dikerjakan peserta (opsional)" value={l.task || ""} onChange={(e) => setLesson(i, { task: e.target.value })} data-testid={`lesson-task-${i}`} />}
                {l.type === "quiz" && <QuizEditor ns={l.id || `new${i}`} quiz={l.quiz} onChange={(quiz) => setLesson(i, { quiz })} />}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, lessons: [...form.lessons, { type: "text", title: "", url: "", content: "" }] })} className="text-xs font-bold text-blue-600 inline-flex items-center gap-1" data-testid="lesson-add-button"><Plus className="w-3.5 h-3.5" /> Tambah Materi</button>
          </div>
        </div>
        <button disabled={saving} className={`${btnPrimary} w-full`} data-testid="course-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Course"}</button>
      </form>
    </div>
  );
}

function CoursesTab() {
  const [courses, setCourses] = useState(null);
  const [categories, setCategories] = useState([]);
  const [growClasses, setGrowClasses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [catName, setCatName] = useState("");
  const load = () => api.get("/admin/lms/courses").then((r) => setCourses(r.data));
  useEffect(() => {
    load();
    api.get("/admin/lms/categories").then((r) => setCategories(r.data));
    api.get("/admin/r/grow_classes").then((r) => setGrowClasses(r.data));
  }, []);
  const del = async (c) => {
    if (!window.confirm(`Hapus course "${c.title}"?`)) return;
    await api.delete(`/admin/lms/courses/${c.id}`);
    load();
  };
  const addCat = async (e) => {
    e.preventDefault();
    if (!catName.trim()) return;
    await api.post("/admin/lms/categories", { name: catName.trim(), order: categories.length + 1 });
    setCatName("");
    api.get("/admin/lms/categories").then((r) => setCategories(r.data));
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setEditing({ ...emptyCourse })} className={`${btnPrimary} !py-2.5 text-sm`} data-testid="course-create-button"><Plus className="w-4 h-4" /> Course Baru</button>
        <form onSubmit={addCat} className="flex gap-2 ml-auto">
          <input className={`${inputCls} !w-44 !py-2 text-sm`} placeholder="Kategori baru" value={catName} onChange={(e) => setCatName(e.target.value)} data-testid="category-input" />
          <button className="text-xs font-bold bg-slate-900 text-white rounded-xl px-4" data-testid="category-add-button">+ Kategori</button>
        </form>
      </div>
      {!courses ? <Skeleton className="h-64" /> : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="courses-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr>{["Course", "Kategori", "Visibilitas", "Materi", "Peserta", "Lulus", "Status", ""].map((h) => <th key={h} className="px-5 py-4 font-bold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {courses.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`course-row-${c.id}`}>
                  <td className="px-5 py-4"><p className="font-bold text-slate-900">{c.title}</p><p className="text-xs text-slate-500">{c.instructor || "—"}</p></td>
                  <td className="px-5 py-4 text-slate-600">{c.category || "—"}</td>
                  <td className="px-5 py-4"><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[c.visibility === "all" ? "info" : "warn"]}`}>{c.visibility === "all" ? "Terbuka" : "Ditugaskan"}</span></td>
                  <td className="px-5 py-4 tabular-nums">{(c.lessons || []).length}</td>
                  <td className="px-5 py-4 tabular-nums">{c.enrollments}</td>
                  <td className="px-5 py-4 tabular-nums">{c.completions}</td>
                  <td className="px-5 py-4"><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[c.published ? "success" : "warn"]}`}>{c.published ? "Terbit" : "Draft"}</span></td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    <button onClick={() => setEditing(c)} className="text-xs font-bold text-blue-600 mr-3" data-testid={`course-edit-${c.id}`}>Edit</button>
                    <button onClick={() => del(c)} className="text-xs font-bold text-red-500" data-testid={`course-delete-${c.id}`}>Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {courses.length === 0 && <p className="p-8 text-center text-slate-500">Belum ada course.</p>}
        </div>
      )}
      {editing && <CourseEditor initial={editing} categories={categories} growClasses={growClasses} allCourses={courses || []} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function AccessTab() {
  const [access, setAccess] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [members, setMembers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [assign, setAssign] = useState({ user_id: "", course_id: "" });
  const load = () => {
    api.get("/admin/lms/access-requests").then((r) => setAccess(r.data));
    api.get("/admin/lms/enrollments").then((r) => setEnrollments(r.data));
  };
  useEffect(() => {
    load();
    api.get("/admin/members").then((r) => setMembers(r.data));
    api.get("/admin/lms/courses").then((r) => setCourses(r.data));
  }, []);
  const decide = async (path, id) => {
    try {
      await api.post(path, { status: id });
      toast.success("Tersimpan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/lms/courses/${assign.course_id}/assign`, { user_id: assign.user_id });
      toast.success("Jemaat ditugaskan ke course");
      setAssign({ user_id: "", course_id: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (!access || !enrollments) return <Skeleton className="h-64" />;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6" data-testid="access-requests-panel">
        <p className="text-sm font-extrabold text-slate-900">Permintaan Akses E-Learning <span className="text-xs font-semibold text-slate-400">· {access.approved_count} jemaat aktif</span></p>
        <div className="mt-3 space-y-2">
          {access.requests.length === 0 && <p className="text-sm text-slate-400 py-2">Tidak ada permintaan baru.</p>}
          {access.requests.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-2xl px-4 py-3" data-testid={`access-request-${r.id}`}>
              <div className="flex-1 min-w-40"><p className="text-sm font-bold text-slate-900">{r.name} <span className="text-xs font-semibold text-slate-500">· {r.nig || "—"}</span></p>{r.reason && <p className="text-xs text-slate-500">"{r.reason}"</p>}</div>
              <button onClick={() => decide(`/admin/lms/access/${r.user_id}/decide`, "approved")} className="text-xs font-bold bg-emerald-500 text-white rounded-full px-4 py-2 inline-flex items-center gap-1" data-testid={`access-approve-${r.id}`}><Check className="w-3.5 h-3.5" /> Setujui</button>
              <button onClick={() => decide(`/admin/lms/access/${r.user_id}/decide`, "rejected")} className="text-xs font-bold text-red-500" data-testid={`access-reject-${r.id}`}>Tolak</button>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={doAssign} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-wrap items-end gap-3" data-testid="assign-form">
        <p className="w-full text-sm font-extrabold text-slate-900">Tugaskan Jemaat ke Course</p>
        <select className={`${inputCls} !w-64`} value={assign.user_id} onChange={(e) => setAssign({ ...assign, user_id: e.target.value })} required data-testid="assign-member-select">
          <option value="">Pilih jemaat…</option>{members.map((m) => <option key={m.user_id} value={m.user_id}>{`${m.name} · ${m.nig || m.email}`}</option>)}
        </select>
        <select className={`${inputCls} !w-64`} value={assign.course_id} onChange={(e) => setAssign({ ...assign, course_id: e.target.value })} required data-testid="assign-course-select">
          <option value="">Pilih course…</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <button className={`${btnPrimary} !py-3 text-sm`} data-testid="assign-submit-button">Tugaskan</button>
      </form>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="enrollments-panel">
        <p className="px-6 pt-6 text-sm font-extrabold text-slate-900">Pendaftaran Course</p>
        <table className="w-full text-sm mt-3">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr>{["Jemaat", "Course", "Progress", "Status", ""].map((h) => <th key={h} className="px-5 py-3 font-bold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {enrollments.map((e) => (
              <tr key={e.id} data-testid={`enrollment-row-${e.id}`}>
                <td className="px-5 py-3"><p className="font-bold text-slate-900">{e.name}</p><p className="text-xs text-slate-500">{e.nig || "—"}</p></td>
                <td className="px-5 py-3 text-slate-600">{e.course_title}</td>
                <td className="px-5 py-3 tabular-nums">{e.percent}%{e.completed && <GraduationCap className="w-4 h-4 inline ml-1 text-emerald-500" />}</td>
                <td className="px-5 py-3"><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[e.status === "approved" ? "success" : e.status === "pending" ? "warn" : "danger"]}`}>{e.status === "approved" ? "Disetujui" : e.status === "pending" ? "Menunggu" : "Ditolak"}</span></td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {e.status === "pending" && (
                    <>
                      <button onClick={() => decide(`/admin/lms/enrollments/${e.id}/decide`, "approved")} className="text-xs font-bold text-emerald-600 mr-3" data-testid={`enrollment-approve-${e.id}`}>Setujui</button>
                      <button onClick={() => decide(`/admin/lms/enrollments/${e.id}/decide`, "rejected")} className="text-xs font-bold text-red-500" data-testid={`enrollment-reject-${e.id}`}>Tolak</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {enrollments.length === 0 && <p className="p-8 text-center text-slate-500">Belum ada pendaftaran.</p>}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [courses, setCourses] = useState(null);
  useEffect(() => { api.get("/admin/lms/courses").then((r) => setCourses(r.data)); }, []);
  if (!courses) return <Skeleton className="h-64" />;
  const certs = courses.reduce((a, c) => a + c.completions, 0);
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="lms-reports">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2"><BookOpenCheck className="w-4 h-4 text-blue-600" /> Ringkasan Course</p>
        <div className="mt-4 space-y-3">
          {courses.map((c) => (
            <div key={c.id}>
              <div className="flex justify-between text-xs font-bold"><span className="text-slate-700">{c.title}</span><span className="text-slate-400">{c.completions}/{c.enrollments} lulus</span></div>
              <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${c.enrollments ? (c.completions * 100) / c.enrollments : 0}%` }} /></div>
            </div>
          ))}
          {courses.length === 0 && <p className="text-sm text-slate-400">Belum ada data.</p>}
        </div>
      </div>
      <div className="bg-slate-900 text-white rounded-3xl p-6">
        <p className="text-sm font-extrabold flex items-center gap-2"><ListChecks className="w-4 h-4 text-amber-300" /> Sertifikat Terbit</p>
        <p className="mt-4 text-5xl font-black" data-testid="cert-count">{certs}</p>
        <p className="mt-2 text-xs text-white/50">Verifikasi publik: {window.location.origin}/verify/&lt;nomor-sertifikat&gt;</p>
        <p className="mt-1 text-[11px] text-white/30">Update {fmtDate(new Date().toISOString())}</p>
      </div>
    </div>
  );
}

function CertificateTab() {
  const [cfg, setCfg] = useState(null);
  const [target, setTarget] = useState("name");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const tplRef = useRef(null);
  const fontRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => { api.get("/admin/lms/certificate/config").then((r) => setCfg(r.data)); }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post("/admin/lms/certificate/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(data.field, data.url);
      toast.success(kind === "template" ? "Template terunggah" : "Font terunggah");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(["name_x", "name_y", "name_size", "number_x", "number_y", "number_size"].map((k) => [k, cfg[k] === "" || cfg[k] === null ? null : Number(cfg[k])]));
      await api.put("/admin/lms/certificate/config", { ...cfg, ...payload });
      toast.success("Pengaturan sertifikat tersimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const genPreview = async () => {
    setBusy(true);
    try {
      await save();
      const res = await api.post("/admin/lms/certificate/preview", { name: "Nama Lengkap Jemaat" }, { responseType: "blob" });
      setPreview(URL.createObjectURL(res.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onTemplateClick = (e) => {
    const img = imgRef.current;
    if (!img || !nat.w) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) * (nat.w / rect.width));
    const y = Math.round((e.clientY - rect.top) * (nat.h / rect.height));
    set(`${target}_x`, x);
    set(`${target}_y`, y);
    toast.success(`${target === "name" ? "Nama" : "Nomor"}: X=${x}, Y=${y}`);
  };

  if (!cfg) return <Skeleton className="h-64" />;
  const marker = (key, color, label) => {
    if (cfg[`${key}_x`] == null || cfg[`${key}_y`] == null || !nat.w) return null;
    return <div className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${(cfg[`${key}_x`] / nat.w) * 100}%`, top: `${(cfg[`${key}_y`] / nat.h) * 100}%` }}>
      <div className="w-3 h-3 rounded-full ring-2 ring-white" style={{ background: color }} />
      <span className="absolute left-4 -top-1 text-[10px] font-black px-1.5 py-0.5 rounded text-white whitespace-nowrap" style={{ background: color }}>{label}</span>
    </div>;
  };

  const CoordRow = ({ k, title, dot }) => (
    <div className={`rounded-2xl border p-3 ${target === k ? "border-blue-500 bg-blue-50/50" : "border-slate-200"}`} data-testid={`cert-coord-${k}`}>
      <label className="flex items-center gap-2 text-sm font-bold text-slate-800">
        <input type="radio" name="cert-target" checked={target === k} onChange={() => setTarget(k)} data-testid={`cert-target-${k}`} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: dot }} /> {title}
        {target === k && <span className="ml-auto text-[10px] font-bold text-blue-600">← klik gambar untuk set posisi</span>}
      </label>
      <div className="mt-2 grid grid-cols-4 gap-2">
        <label className="text-[11px] font-bold text-slate-500">X<input type="number" className={`${inputCls} !p-2 !mt-1`} value={cfg[`${k}_x`] ?? ""} placeholder="tengah" onChange={(e) => set(`${k}_x`, e.target.value)} data-testid={`cert-${k}-x`} /></label>
        <label className="text-[11px] font-bold text-slate-500">Y<input type="number" className={`${inputCls} !p-2 !mt-1`} value={cfg[`${k}_y`] ?? ""} onChange={(e) => set(`${k}_y`, e.target.value)} data-testid={`cert-${k}-y`} /></label>
        <label className="text-[11px] font-bold text-slate-500">Ukuran<input type="number" className={`${inputCls} !p-2 !mt-1`} value={cfg[`${k}_size`] ?? ""} onChange={(e) => set(`${k}_size`, e.target.value)} data-testid={`cert-${k}-size`} /></label>
        <label className="text-[11px] font-bold text-slate-500">Warna<input type="color" className="w-full h-9 mt-1 rounded-lg border border-slate-200" value={cfg[`${k}_color`] || "#000000"} onChange={(e) => set(`${k}_color`, e.target.value)} data-testid={`cert-${k}-color`} /></label>
      </div>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="cert-tab">
      <div className="space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
          <p className="text-sm font-extrabold text-slate-900">Template & Font</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => tplRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold bg-slate-900 text-white rounded-xl px-4 py-2.5 disabled:opacity-60" data-testid="cert-upload-template"><Upload className="w-4 h-4" /> Unggah Template (PNG/JPG)</button>
            <input ref={tplRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { upload("template", e.target.files?.[0]); e.target.value = ""; }} data-testid="cert-template-input" />
            <button onClick={() => fontRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold bg-white border border-slate-200 text-slate-700 rounded-xl px-4 py-2.5 disabled:opacity-60" data-testid="cert-upload-font"><Upload className="w-4 h-4" /> Unggah Font (.ttf)</button>
            <input ref={fontRef} type="file" accept=".ttf,.otf" className="hidden" onChange={(e) => { upload("font", e.target.files?.[0]); e.target.value = ""; }} data-testid="cert-font-input" />
          </div>
          <p className="text-xs text-slate-500">{cfg.font_url ? "Font kustom aktif." : "Memakai font bawaan (serif) bila font tidak diunggah."}</p>
          <label className="block text-xs font-bold text-slate-500">Format Nomor Sertifikat
            <input className={`${inputCls} !mt-1`} value={cfg.number_format || ""} onChange={(e) => set("number_format", e.target.value)} data-testid="cert-number-format" />
            <span className="text-[11px] font-normal text-slate-400">Gunakan {"{year}"} dan {"{seq:04d}"} — contoh: GIM-LMS/{"{year}"}/{"{seq:04d}"}</span>
          </label>
        </div>
        <CoordRow k="name" title="Posisi Nama" dot="#2563eb" />
        <CoordRow k="number" title="Posisi Nomor Sertifikat" dot="#d97706" />
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className={`${btnPrimary} flex-1`} data-testid="cert-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengaturan"}</button>
          <button onClick={genPreview} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-bold bg-amber-500 text-white rounded-2xl px-4 py-3 disabled:opacity-60" data-testid="cert-preview-btn"><Award className="w-4 h-4" /> Pratinjau Hasil</button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4">
          <p className="text-sm font-extrabold text-slate-900 mb-2">Template — klik untuk menempatkan teks</p>
          {cfg.template_url ? (
            <div className="relative inline-block w-full cursor-crosshair" onClick={onTemplateClick} data-testid="cert-template-canvas">
              <img ref={imgRef} src={fileUrl(cfg.template_url)} alt="template" className="w-full rounded-xl border border-slate-100 select-none"
                onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })} draggable={false} />
              {marker("name", "#2563eb", "Nama")}
              {marker("number", "#d97706", "Nomor")}
            </div>
          ) : <p className="text-sm text-slate-400 py-10 text-center">Unggah template terlebih dahulu.</p>}
          {nat.w > 0 && <p className="mt-2 text-[11px] text-slate-400">Dimensi template: {nat.w}×{nat.h} px. Titik yang Anda klik menjadi <b>pusat</b> teks (rata tengah).</p>}
        </div>
        {preview && (
          <div className="bg-slate-900 rounded-3xl p-4" data-testid="cert-preview-result">
            <p className="text-xs font-bold text-white/60 mb-2">Hasil Pratinjau</p>
            <img src={preview} alt="preview" className="w-full rounded-xl" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ELearningAdmin() {
  const [tab, setTab] = useState("courses");
  return (
    <div data-testid="admin-elearning">
      <AdminTitle eyebrow="GIM E-Learning" title="Manajemen LMS" />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="lms-tabs">
        {[["courses", "Courses"], ["access", "Akses & Enrollment"], ["certificate", "Sertifikat"], ["reports", "Laporan"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`lms-tab-${k}`} className={`text-sm font-bold rounded-full px-5 py-2.5 transition ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>
      {tab === "courses" && <CoursesTab />}
      {tab === "access" && <AccessTab />}
      {tab === "certificate" && <CertificateTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}
